"use client";

import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import style from "./productKeyFeatures.module.scss";
import Button from "@/components/design-system/button";
import { useDebouncedCallback } from "use-debounce";

type ProductKeyFeaturesProps = {
  productDetails: ProductDetails;
  className?: string;
};

function ProductKeyFeatures({ productDetails, className }: ProductKeyFeaturesProps) {
  const [clamped, setClamped] = useState(true);

  const [showButton, setShowButton] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedCheckLoadMoreAvailability = useDebouncedCallback(
    () => {
      checkLoadMoreAvailability();
    },
    50,
    { maxWait: 1000 },
  );

  const handleClick = () => setClamped(!clamped);

  useEffect(() => {
    debouncedCheckLoadMoreAvailability();
    window.addEventListener("resize", onResizeHandler);

    // The following cleans up the resize attachment
    return () => {
      window.removeEventListener("resize", onResizeHandler);
    };
  }, [productDetails.formattedKeyFeatures]);

  const hasClamping = (element: any) => {
    const { clientHeight, scrollHeight } = element;
    return clientHeight !== scrollHeight;
  };

  const onResizeHandler = () => {
    if (debouncedCheckLoadMoreAvailability.isPending()) debouncedCheckLoadMoreAvailability.cancel();

    debouncedCheckLoadMoreAvailability();
  };

  const checkLoadMoreAvailability = () => {
    if (containerRef.current) {
      const hadClampClass = containerRef.current.classList.contains(style.clamp);

      if (!hadClampClass) {
        containerRef.current.classList.add(style.clamp);
      }

      setShowButton(hasClamping(containerRef.current));

      if (!hadClampClass) {
        containerRef.current.classList.remove(style.clamp);
      }
    }
  };

  if (productDetails.formattedKeyFeatures) {
    return (
      <div className={`${style.ProductKeyFeatures} ${className ? className : ""}`}>
        <h2>About This Product</h2>

        <div ref={containerRef} className={`${style.content} ${clamped ? style.clamp : ""}`}>
          <ReactMarkdown>{productDetails.formattedKeyFeatures}</ReactMarkdown>
        </div>

        <Button
          variant="tertiary"
          color="blue"
          size="small"
          showLeftIcon={false}
          showRightIcon={false}
          onClick={handleClick}
          type="button"
          text={`View ${clamped ? "more" : "less"}`}
          className={showButton ? style.viewMore : style.hidden}
        />
      </div>
    );
  } else {
    return null;
  }
}

export default ProductKeyFeatures;
